// SettlementRecords.tsx
// Route: /settlement/records
// Mount in your router: <Route path="/settlement/records" element={<SettlementRecords />} />

import React, { useEffect, useState, useCallback } from "react";
import {
  FaSearch, FaSyncAlt, FaArrowLeft, FaChevronDown, FaChevronUp,
  FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaFilter, FaMoneyBillWave, FaUser, FaTruck, FaBuilding,
  FaFileInvoiceDollar, FaCalendarAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type PaymentMode = "online" | "cash" | "bank" | "partial";

interface PaymentRecord {
  id: number;
  distribution_id: number;
  settlement_id: number;
  invoice_id: number;
  user_type: string;
  user_name: string;
  amount: number;
  payment_mode: PaymentMode;
  payment_date: string;
  created_at: string;
  invoice_no: string;
  invoice_status: string;
  total_amount: number;
  allocated_amount: number;
}

interface UserSummary {
  user_type: string;
  user_name: string;
  invoice_count: number;
  total_allocated: number;
  total_paid: number;
  total_remaining: number;
}

type TabKey = "payments" | "users";

const USER_TYPE_LABELS: Record<string, string> = {
  sender: "Sender", receiver: "Receiver", broker: "Broker",
  driver: "Driver", consignor: "Consignor", gst: "GST",
  tds: "TDS", profit: "Profit",
};

const MODE_COLORS: Record<PaymentMode, string> = {
  online:  "bg-blue-100 text-blue-700",
  cash:    "bg-green-100 text-green-700",
  bank:    "bg-purple-100 text-purple-700",
  partial: "bg-yellow-100 text-yellow-700",
};

const STATUS_COLORS: Record<string, string> = {
  paid:      "bg-green-100 text-green-700",
  partial:   "bg-yellow-100 text-yellow-700",
  generated: "bg-blue-100 text-blue-700",
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  driver:    <FaTruck className="text-orange-400" />,
  receiver:  <FaBuilding className="text-blue-400" />,
  sender:    <FaBuilding className="text-indigo-400" />,
  broker:    <FaUser className="text-purple-400" />,
  consignor: <FaUser className="text-teal-400" />,
};

function fmt(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}
function fmtDate(d: string) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const SettlementRecords: React.FC = () => {
  const navigate = useNavigate();

  // tab
  const [tab, setTab] = useState<TabKey>("users");

  // payments tab state
  const [payments, setPayments]       = useState<PaymentRecord[]>([]);
  const [payTotal, setPayTotal]       = useState(0);
  const [payPage, setPayPage]         = useState(1);
  const PAY_LIMIT = 30;

  // users tab state
  const [users, setUsers]             = useState<UserSummary[]>([]);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userPayments, setUserPayments] = useState<Record<string, PaymentRecord[]>>({});
  const [userPayLoading, setUserPayLoading] = useState<Record<string, boolean>>({});

  // filters
  const [filterUserType, setFilterUserType] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterStatus,   setFilterStatus]   = useState("");
  const [filterFrom,     setFilterFrom]     = useState("");
  const [filterTo,       setFilterTo]       = useState("");
  const [search,         setSearch]         = useState("");

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // ── fetch payments ──────────────────────────────────────────────────────────
  const fetchPayments = useCallback(async (page = 1) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (filterUserType) params.set("userType", filterUserType);
      if (filterUserName) params.set("userName", filterUserName);
      if (filterStatus)   params.set("status",   filterStatus);
      if (filterFrom)     params.set("from",      filterFrom);
      if (filterTo)       params.set("to",        filterTo);
      params.set("page",  String(page));
      params.set("limit", String(PAY_LIMIT));
      const res = await fetch(`${API_BASE}/settlment?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPayments(data.records ?? []);
      setPayTotal(data.total ?? 0);
      setPayPage(page);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filterUserType, filterUserName, filterStatus, filterFrom, filterTo]);

  // ── fetch user summaries ────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (filterUserType) params.set("userType", filterUserType);
      if (filterStatus)   params.set("status",   filterStatus);
      const res = await fetch(`${API_BASE}/settlment/summary/users-v2?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: UserSummary[] = await res.json();
      setUsers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filterUserType, filterStatus]);

  useEffect(() => {
    if (tab === "payments") fetchPayments(1);
    else fetchUsers();
  }, [tab, fetchPayments, fetchUsers]);

  // ── fetch per-user payment detail ──────────────────────────────────────────
  async function fetchUserPayments(user: UserSummary) {
    const key = `${user.user_type}::${user.user_name}`;
    if (userPayments[key]) { setExpandedUser(key); return; }
    setUserPayLoading((p) => ({ ...p, [key]: true }));
    try {
      const params = new URLSearchParams({ userName: user.user_name, userType: user.user_type, limit: "200", page: "1" });
      const res = await fetch(`${API_BASE}/settlment?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUserPayments((p) => ({ ...p, [key]: data.records ?? [] }));
    } catch {}
    finally { setUserPayLoading((p) => ({ ...p, [key]: false })); }
    setExpandedUser(key);
  }

  // ── filtered payments (client-side name search) ──────────────────────────
  const visiblePayments = search
    ? payments.filter((p) =>
        `${p.user_name} ${p.invoice_no} ${p.user_type}`.toLowerCase().includes(search.toLowerCase())
      )
    : payments;

  const visibleUsers = search
    ? users.filter((u) =>
        `${u.user_name} ${u.user_type}`.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  // ── totals for payments tab ───────────────────────────────────────────────
  const grandTotal = visiblePayments.reduce((s, p) => s + Number(p.amount || 0), 0);

  const totalPages = Math.ceil(payTotal / PAY_LIMIT);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#cfdbff] via-[#edf1fe] to-[#edf1fe] p-2 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/settlement")}
              className="p-2 rounded-xl bg-white border-2 border-blue-200 text-blue-700 hover:bg-blue-600 hover:text-white transition shadow">
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-3xl lg:text-4xl font-black text-black tracking-tight">Settlement Records</h1>
              <p className="text-blue-700 font-semibold text-sm mt-0.5">Full payment history — by user, driver, company</p>
            </div>
          </div>
          <button
            onClick={() => tab === "payments" ? fetchPayments(payPage) : fetchUsers()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-bold border-2 border-blue-600 hover:bg-black transition shadow disabled:opacity-60">
            {loading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />} Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {([
            { key: "users",    label: "By Person / Company", icon: <FaUser /> },
            { key: "payments", label: "All Payment Records",  icon: <FaFileInvoiceDollar /> },
          ] as { key: TabKey; label: string; icon: React.ReactNode }[]).map((t) => (
            <button key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm border-2 transition shadow-sm ${
                tab === t.key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-700 border-blue-200 hover:border-blue-400"
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border-2 border-blue-100 shadow p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex items-center gap-2 text-blue-700 font-bold text-sm shrink-0">
              <FaFilter /> Filters
            </div>

            {/* search */}
            <div className="relative min-w-[180px] flex-1">
              <FaSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-blue-400" />
              <input type="text" placeholder="Search name / LR / type…"
                className="pl-9 pr-3 py-2 rounded-xl border border-blue-200 text-black text-sm w-full focus:ring-2 ring-blue-300 bg-blue-50/50"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            {/* user type */}
            <select
              className="rounded-xl border border-blue-200 px-3 py-2 text-sm text-black bg-blue-50/50 focus:ring-2 ring-blue-300"
              value={filterUserType} onChange={(e) => setFilterUserType(e.target.value)}>
              <option value="">All Types</option>
              {Object.entries(USER_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>

            {/* status */}
            <select
              className="rounded-xl border border-blue-200 px-3 py-2 text-sm text-black bg-blue-50/50 focus:ring-2 ring-blue-300"
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="generated">Pending</option>
            </select>

            {/* date range (only for payments tab) */}
            {tab === "payments" && (
              <>
                <div className="flex items-center gap-1">
                  <FaCalendarAlt className="text-blue-400 text-xs" />
                  <input type="date" className="rounded-xl border border-blue-200 px-2 py-2 text-sm text-black bg-blue-50/50"
                    value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                </div>
                <span className="text-blue-400 text-sm font-bold">→</span>
                <input type="date" className="rounded-xl border border-blue-200 px-2 py-2 text-sm text-black bg-blue-50/50"
                  value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
              </>
            )}

            <button
              onClick={() => tab === "payments" ? fetchPayments(1) : fetchUsers()}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-800 transition shadow">
              Apply
            </button>
            <button
              onClick={() => {
                setFilterUserType(""); setFilterUserName(""); setFilterStatus("");
                setFilterFrom(""); setFilterTo(""); setSearch("");
                setTimeout(() => tab === "payments" ? fetchPayments(1) : fetchUsers(), 50);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition">
              Clear
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-red-700 font-bold">
            <FaExclamationTriangle /> {error}
          </div>
        )}

        {/* Loading spinner */}
        {loading && (
          <div className="flex justify-center py-16">
            <FaSpinner className="text-blue-600 text-4xl animate-spin" />
          </div>
        )}

        {/* ── USERS TAB ────────────────────────────────────────────────────────── */}
        {!loading && tab === "users" && (
          <div className="flex flex-col gap-4">
            {visibleUsers.length === 0 && (
              <div className="text-center bg-blue-50 py-10 rounded-2xl text-blue-600 font-bold text-lg shadow">
                No settlement records found.
              </div>
            )}

            {/* Summary stats */}
            {visibleUsers.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                {[
                  { label: "Entities", val: visibleUsers.length, cls: "text-blue-700", bg: "bg-blue-50" },
                  { label: "Total Allocated", val: fmt(visibleUsers.reduce((s,u)=>s+Number(u.total_allocated||0),0)), cls: "text-blue-800", bg: "bg-blue-50" },
                  { label: "Total Paid", val: fmt(visibleUsers.reduce((s,u)=>s+Number(u.total_paid||0),0)), cls: "text-green-700", bg: "bg-green-50" },
                  { label: "Total Remaining", val: fmt(visibleUsers.reduce((s,u)=>s+Number(u.total_remaining||0),0)), cls: "text-yellow-700", bg: "bg-yellow-50" },
                ].map((c) => (
                  <div key={c.label} className={`${c.bg} rounded-xl border p-4 shadow-sm`}>
                    <div className="text-xs text-black/50 font-bold uppercase tracking-wider">{c.label}</div>
                    <div className={`text-xl font-black mt-1 ${c.cls}`}>{c.val}</div>
                  </div>
                ))}
              </div>
            )}

            {visibleUsers.map((u) => {
              const key = `${u.user_type}::${u.user_name}`;
              const isExpanded = expandedUser === key;
              const pctPaid = u.total_allocated > 0 ? Math.round((Number(u.total_paid) / Number(u.total_allocated)) * 100) : 0;
              const detail  = userPayments[key] ?? [];
              const isLoadingDetail = userPayLoading[key];

              return (
                <div key={key} className="bg-white rounded-2xl border-2 border-blue-100 shadow hover:shadow-lg transition-all overflow-hidden">
                  {/* Row header */}
                  <button
                    type="button"
                    className="w-full flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 text-left hover:bg-blue-50/50 transition"
                    onClick={() => {
                      if (isExpanded) { setExpandedUser(null); return; }
                      fetchUserPayments(u);
                    }}>
                    {/* Icon + name */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-xl shrink-0 border border-blue-200">
                        {TYPE_ICON[u.user_type] ?? <FaUser className="text-blue-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-extrabold text-blue-900 text-base truncate">{u.user_name || "—"}</div>
                        <div className="text-xs text-blue-500 font-semibold capitalize flex items-center gap-1">
                          <span className="bg-blue-100 rounded px-1.5 py-0.5">{USER_TYPE_LABELS[u.user_type] ?? u.user_type}</span>
                          <span className="text-black/30">·</span>
                          <span>{u.invoice_count} invoice{u.invoice_count !== 1 ? "s" : ""}</span>
                        </div>
                      </div>
                    </div>

                    {/* Amounts */}
                    <div className="flex gap-4 sm:gap-6 text-sm shrink-0">
                      <div className="text-center">
                        <div className="text-xs text-black/40 font-bold">Allocated</div>
                        <div className="font-extrabold text-blue-800">{fmt(u.total_allocated)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-black/40 font-bold">Paid</div>
                        <div className="font-extrabold text-green-700">{fmt(u.total_paid)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-black/40 font-bold">Remaining</div>
                        <div className={`font-extrabold ${Number(u.total_remaining) > 0 ? "text-yellow-700" : "text-green-700"}`}>
                          {fmt(u.total_remaining)}
                        </div>
                      </div>
                    </div>

                    {/* Progress bar + chevron */}
                    <div className="flex items-center gap-3 shrink-0 min-w-[120px]">
                      <div className="flex-1">
                        <div className="text-[10px] text-black/40 font-bold mb-0.5">{pctPaid}% paid</div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden w-24">
                          <div
                            className={`h-full rounded-full transition-all ${pctPaid >= 100 ? "bg-green-500" : pctPaid > 0 ? "bg-blue-500" : "bg-gray-200"}`}
                            style={{ width: `${Math.min(100, pctPaid)}%` }} />
                        </div>
                      </div>
                      {isExpanded ? <FaChevronUp className="text-blue-400" /> : <FaChevronDown className="text-blue-300" />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-blue-100 px-4 pb-5 pt-3 bg-blue-50/40">
                      {isLoadingDetail ? (
                        <div className="flex justify-center py-6">
                          <FaSpinner className="text-blue-500 text-2xl animate-spin" />
                        </div>
                      ) : detail.length === 0 ? (
                        <div className="text-center text-blue-400 italic py-4">No payment records for this entity.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-separate" style={{ borderSpacing: "0 6px" }}>
                            <thead>
                              <tr className="text-left text-xs text-black/40 font-bold uppercase tracking-wider">
                                <th className="px-3 pb-1">Invoice</th>
                                <th className="px-3 pb-1">Date</th>
                                <th className="px-3 pb-1">Amount</th>
                                <th className="px-3 pb-1">Mode</th>
                                <th className="px-3 pb-1">Allocated</th>
                                <th className="px-3 pb-1">Invoice Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.map((rec) => (
                                <tr key={rec.id} className="bg-white shadow-sm rounded-xl">
                                  <td className="px-3 py-2 font-mono font-bold text-sky-700 text-xs rounded-l-xl border-l-4 border-blue-300">
                                    {rec.invoice_no || `#${rec.invoice_id}`}
                                  </td>
                                  <td className="px-3 py-2 text-black/60">{fmtDate(rec.payment_date)}</td>
                                  <td className="px-3 py-2 font-bold text-green-700">{fmt(rec.amount)}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${MODE_COLORS[rec.payment_mode] || "bg-gray-100 text-gray-700"}`}>
                                      {rec.payment_mode}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-blue-700 font-semibold">{fmt(rec.allocated_amount)}</td>
                                  <td className="px-3 py-2 rounded-r-xl">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${STATUS_COLORS[rec.invoice_status] || "bg-gray-100 text-gray-600"}`}>
                                      {rec.invoice_status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {/* Per-entity subtotals */}
                          <div className="mt-3 flex gap-4 text-xs font-bold bg-white rounded-xl px-4 py-2 border border-blue-100 w-fit ml-auto">
                            <span>Total Records: <span className="text-blue-700">{detail.length}</span></span>
                            <span>Total Paid: <span className="text-green-700">{fmt(detail.reduce((s,r)=>s+Number(r.amount||0),0))}</span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── PAYMENTS TAB ─────────────────────────────────────────────────────── */}
        {!loading && tab === "payments" && (
          <div>
            {/* Grand total bar */}
            {visiblePayments.length > 0 && (
              <div className="flex flex-wrap gap-4 mb-4 bg-white rounded-2xl border-2 border-blue-100 px-5 py-3 shadow items-center">
                <FaMoneyBillWave className="text-green-500 text-xl" />
                <span className="font-bold text-black/60 text-sm">Showing {visiblePayments.length} of {payTotal} records</span>
                <span className="ml-auto font-extrabold text-green-700 text-lg">{fmt(grandTotal)} total on this page</span>
              </div>
            )}

            {visiblePayments.length === 0 && (
              <div className="text-center bg-blue-50 py-10 rounded-2xl text-blue-600 font-bold text-lg shadow">
                No payment records found.
              </div>
            )}

            {/* Table */}
            {visiblePayments.length > 0 && (
              <div className="overflow-x-auto rounded-2xl border-2 border-blue-100 shadow bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                    <tr>
                      {["Invoice", "Person", "Type", "Date", "Amount", "Mode", "Allocated", "Invoice Status"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePayments.map((rec, i) => (
                      <tr key={rec.id} className={`border-b border-blue-50 hover:bg-blue-50/60 transition ${i % 2 === 0 ? "bg-white" : "bg-blue-50/20"}`}>
                        <td className="px-4 py-2.5 font-mono font-bold text-sky-700 text-xs">{rec.invoice_no || `#${rec.invoice_id}`}</td>
                        <td className="px-4 py-2.5 font-bold text-blue-900 text-sm">{rec.user_name || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span className="capitalize text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">
                            {USER_TYPE_LABELS[rec.user_type] ?? rec.user_type}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-black/60 text-xs whitespace-nowrap">{fmtDate(rec.payment_date)}</td>
                        <td className="px-4 py-2.5 font-extrabold text-green-700">{fmt(rec.amount)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded font-bold capitalize ${MODE_COLORS[rec.payment_mode] || "bg-gray-100 text-gray-700"}`}>
                            {rec.payment_mode}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-blue-700 font-semibold">{fmt(rec.allocated_amount)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded font-bold capitalize ${STATUS_COLORS[rec.invoice_status] || "bg-gray-100 text-gray-600"}`}>
                            {rec.invoice_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  disabled={payPage <= 1}
                  onClick={() => fetchPayments(payPage - 1)}
                  className="px-4 py-2 rounded-xl bg-white border-2 border-blue-200 text-blue-700 font-bold text-sm hover:bg-blue-600 hover:text-white transition disabled:opacity-40">
                  ← Prev
                </button>
                <span className="text-blue-800 font-bold text-sm px-4">Page {payPage} of {totalPages}</span>
                <button
                  disabled={payPage >= totalPages}
                  onClick={() => fetchPayments(payPage + 1)}
                  className="px-4 py-2 rounded-xl bg-white border-2 border-blue-200 text-blue-700 font-bold text-sm hover:bg-blue-600 hover:text-white transition disabled:opacity-40">
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettlementRecords;